import { createSelector } from 'reselect';

import { SO } from 'services/DB/types';
import * as types from 'constants/cachedItems';
import { TYPE_LOGOUT, TYPE_LOGOUT_SOFT } from 'constants/Login';
import { RESET_SHOPPING_CART } from 'constants/ShoppingCart';
import { TYPE_SET_CLOUD_STATUS } from 'constants/connectivity';

const initState = {
  [SO.PRODUCTS.NAME]: {
    ready: false,
    records: {},
  },
  [SO.PRODUCT_STOCK.NAME]: {
    ready: false,
    records: {},
  },
  [SO.PRICE_LIST.NAME]: {
    ready: false,
    records: {},
  },
  [SO.EMPLOYEES.NAME]: {
    ready: false,
    records: {},
  },
  [SO.CUSTOMERS.NAME]: {
    ready: false,
    records: {},
  },
  [SO.COUPONS.NAME]: {
    ready: false,
    records: {},
  },
  [SO.CAMPAIGNS.NAME]: {
    ready: false,
    records: {},
  },
  [SO.ASSORTMENTS.NAME]: {
    ready: false,
    records: {},
  },
};

export const cachedItems = (state = initState, { type, payload }) => {
  switch (type) {
    case types.ADD_TO_CACHE:
      return {
        ...state,
        [payload.itemType]: {
          ...state[payload.itemType],
          records: { ...state[payload.itemType].records, ...payload.items },
        },
      };
    case types.SET_CACHE:
      return {
        ...state,
        [payload.itemType]: {
          ready: true,
          records: payload.items,
        },
      };
    case TYPE_SET_CLOUD_STATUS:
      if (!state[payload.name]) return state;
      return {
        ...state,
        [payload.name]: {
          ...state[payload.name],
          ready: !payload.statusUpdate.syncing,
        },
      };
    case types.REMOVE_FROM_CACHE:
      // eslint-disable-next-line no-case-declarations
      const newState = { ...state[payload.itemType].records };
      payload.ids.forEach(id => {
        delete newState[id];
      });
      return {
        ...state,
        [payload.itemType]: { ...state[payload.itemType], records: newState },
      };
    case types.RESET_CACHE:
      return { ...state, [payload.itemType]: { ready: false, records: {} } };
    case RESET_SHOPPING_CART:
      return { ...state, [SO.PRODUCTS.NAME]: { ready: false, records: {} } };
    case TYPE_LOGOUT:
    case TYPE_LOGOUT_SOFT:
      return initState;
    default:
      return state;
  }
};

export default cachedItems;

export const getCachedItems = state => state.cachedItems;

export const getCachedItemsPerType = (type: keyof typeof initState) =>
  createSelector(getCachedItems, slice => slice[type]?.records);

export const getCachedProductsLength = createSelector(
  getCachedItemsPerType(SO.PRODUCTS.NAME),
  items => Object.keys(items).length,
);

export const getCachedItemsPerTypeIsReady = (type: keyof typeof initState) =>
  createSelector(getCachedItems, slice => slice[type]?.ready);

export const getCachedItemPerTypeByID = (
  type: keyof typeof initState,
  id: string,
) => state => getCachedItemsPerType(type)(state)[id];

export const getCachedItemsPerTypeByIDs = <
  V extends any,
  K extends string | number | symbol = string
>(
  type: keyof typeof initState,
  ids: K[],
) => (state): Record<K, V> => {
  if (!ids.length) return {} as Record<K, V>;
  const cachedItems = getCachedItemsPerType(type)(state);
  const result = {} as Record<K, V>;
  ids.forEach(id => {
    if (cachedItems[id]) {
      result[id] = cachedItems[id];
    }
  });
  return result;
};
